import clsx from 'clsx';
import React, { useCallback } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import { ProjectColumnConfigFn } from '@interfaces/projects';
import { convertMDLinkToHTML } from '@utils/formatting';
import { getImgixSrc } from '@utils/images';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

export default function useDatatableColumnConfig(
  projectImagePath: string,
  cellConfigFn?: (cellContent: string | JSX.Element, cell) => string | JSX.Element
) {
  const columnConfigFn: ProjectColumnConfigFn = useCallback(
    (column) => {
      return {
        ...column,
        id: column.id,
        enableSorting: column.merge ? false : column.enableSorting,
        header: (head) => {
          if (column.merge) {
            const mergedHeader = head.table.getColumn(column.merge).id;
            return mergedHeader;
          }
          return column.header;
        },
        cell: (cell) => {
          let cellContent: string | JSX.Element = cell.cell.getValue() as string;

          cellContent = convertMDLinkToHTML(cellContent);

          const cellConfig = cell.row.original[`${column.id}_config`] as string;
          if (cellConfig) {
            cellContent = parseCellConfig(cellContent, cellConfig, projectImagePath);
          }

          if (column.merge) {
            const cellToMerge = cell.row.original[column.merge];
            cellContent = mergeVals(cellContent, cellToMerge);
          } else {
            cellContent = parseHtml(cellContent);
          }

          if (cellConfigFn) {
            cellContent = cellConfigFn(cellContent, cell);
          }

          return cellContent;
        },
      };
    },
    [projectImagePath, cellConfigFn]
  );

  return {
    columnConfigFn,
  };
}

const parseHtml = (cellVal: string | JSX.Element): JSX.Element => {
  if (typeof cellVal === 'string') {
    return AuthoredContentReactToHtmlParser(cellVal);
  } else {
    return cellVal;
  }
};

const parseCellConfig = (
  cellContent: string | JSX.Element,
  cellConfig: string,
  projectImagePath: string
): string | JSX.Element => {
  const cellConfigObj = buildCellConfigObject(cellConfig);

  if (cellConfigObj.image) {
    const imageHeight = `h-[${cellConfigObj.imageHeight || 56}px]`;
    const imageWidth = `w-[${cellConfigObj.imageWidth || 56}px]`;

    cellContent = (
      <LazyImgixImage
        role="presentation"
        className={clsx({
          [imageHeight]: !!imageHeight,
          [imageWidth]: !!imageWidth,
        })}
        src={getImgixSrc({
          path: `${projectImagePath}/${cellConfigObj.image}`,
          isEnvDependent: false,
        })}
      />
    );
  }

  return cellContent;
};

interface CellConfig {
  backgroundColor?: string;
  color?: string;
  image?: string;
  imageHeight?: number;
  imageWidth?: number;
}

export const buildCellConfigObject = (cellConfig: string): CellConfig => {
  const configElements = cellConfig.replace(/\s+/g, '').split(';');
  const configObj = configElements.reduce((acc, configElement) => {
    if (!configElement) return acc;

    const [key, value] = configElement.trim().split(':');

    if (Number(value)) {
      acc[key] = Number(value);
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  return configObj;
};

const mergeVals = (leftCell: string | JSX.Element, rightCell: string): JSX.Element => {
  return (
    <div className="flex items-center gap-8">
      <span>{parseHtml(leftCell)}</span>
      <span>{parseHtml(rightCell)}</span>
    </div>
  );
};
